/**
 * SAPPT Design Tokens — Admin Frontend (Indigo brand)
 *
 * 이 파일은 SAPPT 디자인 시스템의 단일 진실 공급원(Single Source of Truth)입니다.
 * 모든 색상/간격/타이포/반경/그림자 값은 여기서 import 해서 사용해야 하며,
 * 컴포넌트 안에 hex 코드를 인라인으로 적지 마세요.
 *
 * 4px 그리드를 따르며 partner-frontend/src/theme/tokens.ts 와 키 구조가 동일합니다.
 * (브랜드 색만 다름)
 */

// ──────────────────────────────────────────────
// Brand (Indigo) — Admin 전용
// ──────────────────────────────────────────────
export const brand = {
  primary: '#4f46e5',
  primaryHover: '#6366f1',
  primaryActive: '#4338ca',
  primarySoft: '#a5b4fc',
  primarySoftBg: '#eef2ff',
  primaryGradient: 'linear-gradient(135deg, #4f46e5, #6366f1)',
  primaryShadow: '0 2px 8px rgba(79,70,229,0.25)',
  primaryRing: 'rgba(79,70,229,0.08)',
} as const;

// ──────────────────────────────────────────────
// Gray Scale (slate 계열)
// ──────────────────────────────────────────────
export const gray = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
} as const;

// ──────────────────────────────────────────────
// State Colors
// ──────────────────────────────────────────────
export const state = {
  success: '#10b981',
  successSoft: '#dcfce7',
  successText: '#15803d',
  warning: '#f59e0b',
  warningSoft: '#fef3c7',
  warningText: '#92400e',
  error: '#ef4444',
  errorSoft: '#fee2e2',
  errorText: '#b91c1c',
  info: '#0ea5e9',
  infoSoft: '#e0f2fe',
  infoText: '#0369a1',
} as const;

// ──────────────────────────────────────────────
// Spacing — 4px Grid
// ──────────────────────────────────────────────
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  base: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64,
} as const;

// ──────────────────────────────────────────────
// Border Radius
// ──────────────────────────────────────────────
export const radius = {
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 20,
  full: 9999,
} as const;

// ──────────────────────────────────────────────
// Shadows
// ──────────────────────────────────────────────
export const shadow = {
  sm: '0 1px 2px rgba(0,0,0,0.04)',
  md: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.03)',
  lg: '0 4px 16px rgba(0,0,0,0.08)',
  xl: '0 8px 28px rgba(0,0,0,0.12)',
  brand: '0 4px 20px rgba(79,70,229,0.25)',
} as const;

// ──────────────────────────────────────────────
// Typography
// ──────────────────────────────────────────────
export const font = {
  family: "'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  mono: "'D2Coding', 'Menlo', 'Consolas', ui-monospace, monospace",
  size: {
    xs: 11,
    sm: 12,
    base: 14,
    md: 15,
    lg: 16,
    xl: 18,
    '2xl': 22,
    '3xl': 28,
  },
  weight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.4,
    base: 1.6,
    relaxed: 1.8,
  },
} as const;

// ──────────────────────────────────────────────
// Semantic aliases (의미 기반 단축 키)
// ──────────────────────────────────────────────
export const color = {
  bg: '#ffffff',
  bgLayout: gray[100],
  bgSubtle: gray[50],
  bgElevated: '#ffffff',
  border: gray[200],
  borderSubtle: gray[100],
  text: gray[800],
  textSecondary: gray[500],
  textTertiary: gray[400],
  textOnPrimary: '#ffffff',
} as const;

// ──────────────────────────────────────────────
// Semantic Colors (의미 기반)
// ──────────────────────────────────────────────
export const semantic = {
  // 차트 팔레트
  chart: ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6'],
  // 아이콘 강조색 (MasterPage 등)
  iconBlue:   '#0ea5e9',
  iconGreen:  '#10b981',
  iconAmber:  '#f59e0b',
  iconRed:    '#ef4444',
  iconPurple: '#8b5cf6',
  iconTeal:   '#14b8a6',
} as const;

// ──────────────────────────────────────────────
// 모달 표준 폭
// ──────────────────────────────────────────────
export const modalWidth = {
  xs:  380,
  sm:  480,
  md:  600,
  lg:  800,
  xl:  1000,
  '2xl': 1200,
} as const;

// ──────────────────────────────────────────────
// 레이아웃 상수
// ──────────────────────────────────────────────
export const layout = {
  headerHeight: 56,
  sidebarWidth: 230,
  sidebarCollapsed: 60,
} as const;

export const tokens = { brand, gray, state, space, radius, shadow, font, color, semantic, modalWidth, layout } as const;
export type Tokens = typeof tokens;
